import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, ArrowRight, Shield, Fan, Wind } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const FansBlowers = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["services/fans-blowers"]?.title} description={seoData["services/fans-blowers"]?.description} path="/services/fans-blowers" keywords={seoData["services/fans-blowers"]?.keywords || []} />

      <PageHero
        badge="Machinery & Equipment"
        title="Fan & Blower Repair"
        description="Industrial fan and blower repair using erosion-resistant and corrosion-resistant coating systems. Impeller rebuild, housing restoration, and inlet cone repair. Extend fan service life without replacement."
        backgroundKey="fans-blowers"
        buttonText="Get In Touch"
      />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Problems We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Impeller erosion from particulate-laden airstreams',
              'Housing corrosion and wall thinning',
              'Inlet cone and throat wear',
              'Vibration damage from imbalanced impellers',
              'Shaft and bearing seat wear',
              'Stack and ductwork corrosion'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-sky-50 rounded-lg">
                <Fan className="text-sky-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border-l-4 border-l-sky-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Wind className="text-sky-600 mr-3 flex-shrink-0" size={22} />
                  Impeller Repair & Protection
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Rebuild eroded impeller blades and leading edges using Belzona ceramic-filled composites. Apply erosion-resistant coatings to extend impeller life between maintenance cycles.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-sky-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Wind className="text-sky-600 mr-3 flex-shrink-0" size={22} />
                  Housing & Scroll Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Fan housing wall rebuild, scroll repair, and inlet cone restoration using metal repair composites. Restore housing integrity and aerodynamic profile.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-sky-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Wind className="text-sky-600 mr-3 flex-shrink-0" size={22} />
                  Shaft & Bearing Restoration
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Shaft scoring repair, bearing seat rebuild, and keyway restoration for fan drive systems. Return rotating components to OEM tolerances.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-sky-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Wind className="text-sky-600 mr-3 flex-shrink-0" size={22} />
                  Erosion Protection Coatings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Ceramic-filled and elastomeric coating systems for long-term erosion protection of fans, blowers, and ductwork handling abrasive or corrosive gases.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose BCS</h2>
              <div className="space-y-3">
                {['Extend impeller and housing life 3-5x between replacements',
                  'In-situ repair — no fan removal required for many repairs',
                  'Dynamic balancing available after impeller repair',
                  'Erosion-resistant coatings for harsh air handling service',
                  'Fast turnaround for shutdown and outage work',
                  '50-70% cost savings vs. new impeller or fan replacement',
                  'Emergency repair for critical ventilation systems',
                  'All fan types — centrifugal, axial, and mixed flow'].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-blue-900 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-xl font-bold mb-4">Belzona Products We Use</h3>
              <div className="space-y-3">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 1321 (Ceramic S-Metal)</p>
                  <p className="text-xs text-slate-500 mt-1">Erosion-resistant impeller coating</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 1111 (Super Metal)</p>
                  <p className="text-xs text-slate-500 mt-1">Fan housing and blade rebuild</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 1391 (Ceramic HT)</p>
                  <p className="text-xs text-slate-500 mt-1">High-temperature fan erosion protection</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 2131 (D&A Fluid Elastomer)</p>
                  <p className="text-xs text-slate-500 mt-1">Flexible erosion protection coating</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Fan Wearing Out? We Rebuild It.</h2>
          <p className="text-blue-100 mb-8">Contact us for impeller, housing, or fan system repair. Emergency service available for critical ventilation.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Assessment <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FansBlowers;
